import './OrderInformation.scss';
import { formatPrice } from "../../utils/FormatPrice";
import { formatRelativeDate, formatFullDate } from "../../utils/FormatDate";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faHourglassStart,
  faLock,
  faStar,
  faUserSlash,
  faUserPlus,
  faCheck,
  faTimes,
  faPen,
  faInfoCircle,
  faUser,
  faPills,
  faCommentDots,
  faCoins,
} from '@fortawesome/free-solid-svg-icons';
import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import axios from 'axios';
import OrderItems from './OrderItems';
import OrderNotes from './OrderNotes';
import InputPatient from '../Form/Fields/InputPatient';
import PatientImage from '../Patient/PatientImage';
import PaymentMethodBadge from './PaymentMethodBadge';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';

// order sheet panels: brand-blue title icons, like the product sheet's
const SECTION_ICON_STYLE = { background: '#eff6ff', color: '#2563eb' };

const statusMap = {
  COMPLETE: { icon: faCheckCircle,   label: 'Terminé',    variant: 'COMPLETE' },
  PENDING:  { icon: faHourglassStart, label: 'En attente', variant: 'PENDING'  },
  CANCELED: { icon: faLock,           label: 'Annulé',     variant: 'CANCELED' },
};


const SectionCard = ({ title, icon, count, action, children }) => (
  <div className="order-section-card">
    {title && (
      <div className="order-section-title">
        <span className="order-section-title-main">
          {icon && (
            <span className="order-section-icon" style={SECTION_ICON_STYLE}>
              <FontAwesomeIcon icon={icon} />
            </span>
          )}
          {title}
          {count != null && <span className="order-count-badge">{count}</span>}
        </span>
        {action && <span className="order-section-title-action">{action}</span>}
      </div>
    )}
    {children}
  </div>
);

const FieldRow = ({ label, children, muted = false }) => (
  <div className="order-field-row">
    <span className="order-field-label">{label}</span>
    <span className={`order-field-value${muted ? ' order-field-value--muted' : ''}`}>
      {children}
    </span>
  </div>
);

const AssignPatientForm = ({ orderId, onRefresh, onCancel }) => {
  const methods = useForm({ defaultValues: { patientId: null } });
  const { handleSubmit, watch } = methods;
  const [saving, setSaving] = useState(false);

  const patientId = watch('patientId');

  const onSubmit = async (data) => {
    if (!data.patientId) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/api/v1/orders/${orderId}/patient`,
        { patientId: data.patientId },
        { withCredentials: true }
      );
      onRefresh();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '0.75rem 1.25rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <InputPatient name="patientId" />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-sm btn-light" onClick={onCancel}>
            Annuler
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={saving || !patientId}>
            Confirmer
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

const OrderOverview = ({ order, onRefresh }) => {
  const [assigning, setAssigning] = useState(false);
  const { hasRight } = useAuth();
  // The backend endpoint behind this (PATCH /orders/{id}/patient) is gated on ORDERS_CREATE
  // (no dedicated "edit order" right exists) — without this the button would sit there and
  // silently fail on submit for a user who can view but not create orders.
  const canAssignPatient = hasRight('ORDERS_CREATE');
  const status = statusMap[order.status];
  const variant = status?.variant || 'UNKNOWN';

  const hasDiscount = order.discountAmount !== null && order.discountAmount !== undefined;
  const hasLoyalty = order.loyaltyPointsUsed > 0 && order.loyaltyDiscountAmount > 0;
  const showRemaining = order.status === 'PENDING' && order.remainingAmount;

  const paymentMethods = [...new Set((order.invoices || []).map(inv => inv.paymentMethod).filter(Boolean))];

  const cashInvoices = (order.invoices || []).filter(inv => inv.paymentMethod === 'CASH' && inv.cashGiven != null);
  const totalCashGiven = cashInvoices.reduce((sum, inv) => sum + parseFloat(inv.cashGiven || 0), 0);
  const totalCashPaid  = cashInvoices.reduce((sum, inv) => sum + parseFloat(inv.totalAmount || 0), 0);
  const cashChange     = totalCashGiven - totalCashPaid;
  const showCash       = cashInvoices.length > 0;

  return (
    <div className="order-info-wrapper">
      <div className="order-col-layout">

        {/* Colonne gauche */}
        <div>
          <SectionCard title="Informations" icon={faInfoCircle}>
            <FieldRow label="Référence">{order.reference}</FieldRow>
            <FieldRow label="Date de création">
              <span
                data-tooltip-id="tooltip"
                data-tooltip-content={formatFullDate(order.createdAt)}
              >
                {formatRelativeDate(order.createdAt)}
              </span>
            </FieldRow>
            {paymentMethods.length > 0 && (
              <FieldRow label="Paiement">
                {paymentMethods.map((m, i) => {
                  const labels = { CASH: 'Espèces', CARD: 'Carte', CHECKS_PAYMENT: 'Chèque', SPLIT_PAYMENT: 'À crédit' };
                  return (
                    <span key={m} style={{ color: '#1a1a1a', fontSize: 14 }}>
                      {i > 0 && ', '}{labels[m] ?? m}
                    </span>
                  );
                })}
              </FieldRow>
            )}
            <FieldRow label="Statut">
              <span className={`order-status-badge order-status-badge--${variant}`}>
                {status?.icon && <FontAwesomeIcon icon={status.icon} />}
                {status?.label || 'Unknown'}
              </span>
            </FieldRow>
          </SectionCard>
        </div>

        {/* Colonne droite */}
        <div>
          <SectionCard
            title="Patient"
            icon={faUser}
            action={!assigning && canAssignPatient && (
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 11, padding: '2px 8px' }}
                onClick={() => setAssigning(true)}
              >
                <FontAwesomeIcon icon={order.patient ? faPen : faUserPlus} style={{ marginRight: 4 }} />
                {order.patient ? 'Modifier' : 'Assigner'}
              </button>
            )}
          >
            {assigning ? (
              <AssignPatientForm
                orderId={order.id}
                onRefresh={() => { setAssigning(false); onRefresh && onRefresh(); }}
                onCancel={() => setAssigning(false)}
              />
            ) : order.patient ? (
              <>
                <FieldRow label="Nom">
                  <NavLink to={`/patient/${order.patient.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <PatientImage patient={order.patient} size={28} />
                    {order.patient.firstName} {order.patient.lastName}
                  </NavLink>
                </FieldRow>
                <FieldRow label="Email">
                  {order.patient.email || '—'}
                </FieldRow>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem 1.25rem', color: '#9ca3af' }}>
                <FontAwesomeIcon icon={faUserSlash} style={{ fontSize: 14 }} />
                <span style={{ fontSize: 13 }}>Visiteur</span>
              </div>
            )}
          </SectionCard>
        </div>

      </div>

      <SectionCard title="Articles" icon={faPills} count={order.items?.length ?? 0}>
        <OrderItems items={order.items} />
      </SectionCard>

      <div className="order-col-layout">

        {/* Commentaires */}
        <SectionCard title="Commentaires" icon={faCommentDots}>
          <OrderNotes orderId={order.id} currentStatus={order.status} onRefresh={onRefresh} />
        </SectionCard>

        {/* Détail financier */}
        <SectionCard title="Détail financier" icon={faCoins}>
          <FieldRow label="Sous-total">{formatPrice(order.subtotal)}</FieldRow>
          <FieldRow label="Remise">
            {hasDiscount ? formatPrice(order.discountAmount) : formatPrice(0)}
            {order.discountType === 'PERCENT' && (
              <span className="order-field-value--muted" style={{ marginLeft: 6, fontSize: 11 }}>
                ({order.discountValue}%)
              </span>
            )}
          </FieldRow>
          {hasLoyalty && (
            <FieldRow label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <FontAwesomeIcon icon={faStar} style={{ color: '#b45309', fontSize: 11 }} />
                Points fidélité
              </span>
            }>
              <span style={{ color: '#b45309' }}>
                -{formatPrice(order.loyaltyDiscountAmount)}
                <span style={{ fontSize: 11, color: '#d97706', marginLeft: 4 }}>
                  ({order.loyaltyPointsUsed} pts)
                </span>
              </span>
            </FieldRow>
          )}
          <FieldRow label="Total">
            <strong style={{ color: '#1e40af' }}>{formatPrice(order.total)}</strong>
          </FieldRow>
          {showRemaining && (
            <FieldRow label="Reste à payer">
              <strong style={{ color: '#dc2626' }}>{formatPrice(order.remainingAmount)}</strong>
            </FieldRow>
          )}
          {showCash && (
            <FieldRow label="Reçu">
              {formatPrice(totalCashGiven)}
            </FieldRow>
          )}
          {showCash && (
            <FieldRow label="Rendu">
              <span style={{ color: cashChange >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                {formatPrice(cashChange)}
              </span>
            </FieldRow>
          )}
        </SectionCard>

      </div>
    </div>
  );
};

export default OrderOverview;
